import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteMeta, chapters } from "@/mocks/manga";

const panelImages = [
  "https://readdy.ai/api/search-image?query=Romance%20manga%20comic%20panel%20artwork%20of%20a%20young%20couple%20having%20a%20tender%20conversation%20in%20a%20sunlit%20room%2C%20soft%20rose%20and%20warm%20gold%20palette%2C%20gentle%20expression%2C%20delicate%20line%20art%2C%20cinematic%20lighting%2C%20detailed%20illustration%2C%20landscape%20composition&width=1400&height=880&seq=read-panel-1&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Romance%20manga%20comic%20panel%20artwork%20of%20a%20young%20couple%20standing%20close%20under%20falling%20petals%20at%20dusk%2C%20emotional%20tender%20moment%2C%20warm%20orange%20and%20rose%20tones%2C%20delicate%20line%20art%2C%20soft%20bokeh%20background%2C%20high%20quality%20illustration%2C%20landscape%20composition&width=1400&height=880&seq=read-panel-2&orientation=landscape",
];

const badgeClass: Record<string, string> = {
  单行本: "bg-primary-100 text-primary-700",
  连载: "bg-accent-100 text-accent-700",
  全彩: "bg-secondary-200 text-secondary-800",
  番外: "bg-primary-100 text-primary-700",
};

export default function Read() {
  const { id } = useParams();
  const index = chapters.findIndex((c) => c.id === id);
  const chapter = index >= 0 ? chapters[index] : chapters[0];
  const safeIndex = index >= 0 ? index : 0;
  const prev = safeIndex > 0 ? chapters[safeIndex - 1] : null;
  const next = safeIndex < chapters.length - 1 ? chapters[safeIndex + 1] : null;
  const sameGroup = chapters.filter((c) => c.category === chapter.category && c.id !== chapter.id).slice(0, 6);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${chapter.title} - ${siteMeta.name}`,
    description: chapter.desc,
    articleSection: chapter.category,
  };

  return (
    <>
      <Seo
        title={`${chapter.title} - 甜蜜惩罚漫画 | ${siteMeta.name}`}
        description={`${chapter.title}：${chapter.desc} 收录于${siteMeta.name}阅读目录。`}
        keywords={siteMeta.keywords}
        canonicalPath={`/read/${chapter.id}`}
        schema={articleSchema}
      />

      <div className="mx-auto max-w-4xl px-4 pb-16 pt-24 md:px-6 md:pt-28">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "阅读目录", to: "/catalog" },
            { label: chapter.title },
          ]}
        />

        <header className="mt-6 rounded-2xl bg-background-100 p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badgeClass[chapter.category]}`}>
              {chapter.category}
            </span>
            <span className="rounded-full bg-background-50 px-3 py-1 text-xs font-semibold text-foreground-600">
              {chapter.volume}
            </span>
            {chapter.isColor && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
                <i className="ri-palette-line text-xs"></i>
                全彩
              </span>
            )}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{chapter.title}</h1>
          <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">{chapter.desc}</p>
        </header>

        <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="space-y-6 md:col-span-2">
            <section aria-label="章节正文">
              <p className="text-[15px] leading-8 text-foreground-700">
                《甜蜜惩罚》的这段故事里，人物之间细腻的试探与温柔的交锋交织在一起。随着彼此的了解逐渐加深，
                原本由契约维系的距离，也在一次次不经意的靠近中慢慢消融。每一个眼神、每一句欲言又止的话语，
                都藏着尚未说出口的心意，让这段关系在甜蜜与紧张之间摇摆。
              </p>
              <div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-background-200/70">
                <img src={panelImages[0]} alt={`${chapter.title} 场景插画一`} className="w-full object-cover object-top" />
              </div>
              <p className="mt-6 text-[15px] leading-8 text-foreground-700">
                当故事推进到这里，真正的情感逐渐浮出水面。过去被掩盖的秘密、被忽略的感受，都在这一刻被温柔地正视。
                两人终于学会不再逃避，而是并肩面对那份既让人心跳、也让人安心的答案。
              </p>
              <div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-background-200/70">
                <img src={panelImages[1]} alt={`${chapter.title} 场景插画二`} className="w-full object-cover object-top" />
              </div>
              <p className="mt-6 text-[15px] leading-8 text-foreground-700">
                这一段故事为整部作品的情感脉络埋下了温暖的伏笔。无论是初次相遇的新读者，
                还是想要重温名场面的老读者，都能在这份细腻的叙述中，再次感受到属于《甜蜜惩罚》的独特心动。
              </p>
            </section>

            <nav className="flex flex-col gap-3 sm:flex-row" aria-label="章节切换">
              {prev ? (
                <Link
                  to={`/read/${prev.id}`}
                  className="flex flex-1 items-center gap-3 rounded-2xl bg-background-100 p-4 transition-colors hover:bg-primary-100/70"
                >
                  <i className="ri-arrow-left-s-line text-xl text-primary-600"></i>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500">上一篇</span>
                    <span className="block truncate text-sm font-semibold text-foreground-900">{prev.title}</span>
                  </span>
                </Link>
              ) : (
                <span className="flex-1"></span>
              )}
              {next ? (
                <Link
                  to={`/read/${next.id}`}
                  className="flex flex-1 items-center justify-end gap-3 rounded-2xl bg-background-100 p-4 text-right transition-colors hover:bg-primary-100/70"
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500">下一篇</span>
                    <span className="block truncate text-sm font-semibold text-foreground-900">{next.title}</span>
                  </span>
                  <i className="ri-arrow-right-s-line text-xl text-primary-600"></i>
                </Link>
              ) : (
                <span className="flex-1"></span>
              )}
            </nav>
          </div>

          <aside>
            <h2 className="font-heading text-base font-bold text-foreground-950">本分类更多章节</h2>
            <ul className="mt-4 space-y-2">
              {sameGroup.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/read/${c.id}`}
                    className="flex items-center gap-3 rounded-xl bg-background-100 px-4 py-3 text-sm text-foreground-700 transition-colors hover:bg-primary-100/70 hover:text-primary-700"
                  >
                    <i className="ri-book-open-line text-primary-500"></i>
                    <span className="truncate">{c.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/catalog"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
            >
              返回阅读目录
              <i className="ri-arrow-right-line"></i>
            </Link>
          </aside>
        </div>
      </div>
    </>
  );
}